"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useFormSubmission } from "@/hooks/use-form-submission";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { InputGroup, InputGroupTextarea } from "@/components/ui/input-group";
import { formSchema, FormSchemaType } from "@/lib/form-schema";
import { PhoneInput } from "../reui/phone-input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useServiceList } from "@/hooks/use-service-list";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import useConstantValueList from "@/hooks/use-constant-value-list";
import { RenderIf } from "../common/render-if";
import { Checkbox } from "../ui/checkbox";
import { cn } from "@/lib/utils";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import * as z from "zod";

const enquiryFormSchema = formSchema
  .pick({
    fullName: true,
    email: true,
    contactNumber: true,
    message: true,
    teamSize: true,
    whoAreWe: true,
    enquiryType: true,
    serviceId: true,
    terms: true,
  })
  .extend({
    serviceId: z.array(z.union([z.string(), z.number()])).min(1, "Please select a course"),
  });

type EnquiryFormProps = {
  isSingleColumn?: boolean;
  serviceId?: string | number;
  onSuccess?: () => void;
  isCourseVisible?: boolean;
  title?: string;
  description?: string;
  className?: string;
};

export function EnquiryForm({
  serviceId,
  isSingleColumn,
  onSuccess,
  isCourseVisible,
  title,
  description,
  className,
}: EnquiryFormProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { data: courses } = useServiceList();

  const { data: whoWeAre } = useConstantValueList();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(enquiryFormSchema as any),
    defaultValues: {
      fullName: "",
      email: "",
      contactNumber: "",
      message: "",
      teamSize: "1",
      whoAreWe: "173",
      serviceId: serviceId ? [serviceId] : [],
      enquiryType: pathname.includes("contact-us") ? "CONTACT_US_FORM" : "COURSE_REQUEST_TRAINING",
      terms: false,
    },
  });

  const selectedWhoWeAre = useWatch({
    control: form.control,
    name: "whoAreWe",
  });

  const { submit, isSubmitting } = useFormSubmission({
    setError: form.setError,
    onSuccess: ({ name, message }) => {
      form.reset();
      sessionStorage.setItem("thankYouData", JSON.stringify({ name, message }));
      router.push("/thank-you-for-enquiry");
      if (typeof onSuccess === "function") {
        onSuccess();
      }
    },
  });

  async function onSubmit(data: FormSchemaType) {
    await submit(data);
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title ?? "Enquire us!"}</CardTitle>
        <CardDescription>{description ?? "Drop your query here."}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FieldGroup className={cn(!isSingleColumn ? "grid grid-cols-1 md:grid-cols-2" : "")}>
            <Controller
              name="fullName"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="enquiry-form-fullName">Full Name</FieldLabel>
                  <Input
                    {...field}
                    id="enquiry-form-fullName"
                    aria-invalid={fieldState.invalid}
                    placeholder="Please enter your Full name"
                    autoComplete="off"
                    required
                  />
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <Controller
              name="contactNumber"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="enquiry-form-contactNumber">Phone</FieldLabel>
                  <PhoneInput
                    defaultCountry="US"
                    {...field}
                    id="enquiry-form-contactNumber"
                    aria-invalid={fieldState.invalid}
                    placeholder="Please enter your phone"
                    autoComplete="off"
                    required
                  />
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="enquiry-form-email">Email</FieldLabel>
                  <Input
                    {...field}
                    id="enquiry-form-email"
                    aria-invalid={fieldState.invalid}
                    placeholder="Please enter your email"
                    autoComplete="off"
                    required
                  />
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <RenderIf condition={serviceId === undefined || isCourseVisible!}>
              <Controller
                name="serviceId"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor="enquiry-form-serviceId">Course</FieldLabel>
                    <Select
                      multiple
                      items={courses}
                      name={field.name}
                      value={field.value}
                      onValueChange={field.onChange}
                      id="enquiry-form-serviceId"
                      required
                    >
                      <SelectTrigger className="w-full max-w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent alignItemWithTrigger={false}>
                        <SelectGroup>
                          {courses.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
            </RenderIf>
            <Controller
              name="whoAreWe"
              control={form.control}
              render={({ field, fieldState }) => {
                const isInvalid = fieldState.invalid;
                return (
                  <FieldSet data-invalid={isInvalid}>
                    <FieldLegend variant="label">Enquiry for</FieldLegend>
                    <RadioGroup
                      name={field.name}
                      value={field.value}
                      onValueChange={field.onChange}
                      aria-invalid={isInvalid}
                      className="flex w-fit"
                    >
                      {whoWeAre?.map((item) => (
                        <Field
                          key={item.value}
                          orientation="horizontal"
                          className="flex items-center gap-2"
                        >
                          <RadioGroupItem
                            value={item.value}
                            id={`enquiry-form-who-we-are-${item.value}`}
                          />
                          <Label htmlFor={`enquiry-form-who-we-are-${item.value}`}>
                            {item.label}
                          </Label>
                        </Field>
                      ))}
                    </RadioGroup>
                    {isInvalid && <FieldError errors={[fieldState.error]} />}
                  </FieldSet>
                );
              }}
            />
            <RenderIf condition={selectedWhoWeAre === "172"}>
              <Controller
                name="teamSize"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor="enquiry-form-teamSize">Team Size</FieldLabel>
                    <Input
                      {...field}
                      type="number"
                      required
                      id="enquiry-form-teamSize"
                      aria-invalid={fieldState.invalid}
                      placeholder="Team size"
                      autoComplete="off"
                    />
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
            </RenderIf>
            <Controller
              name="message"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} className="col-span-1 md:col-span-2">
                  <FieldLabel htmlFor="enquiry-form-message">Message</FieldLabel>
                  <InputGroup>
                    <InputGroupTextarea
                      {...field}
                      id="enquiry-form-message"
                      placeholder="Type your message here..."
                      rows={6}
                      className="min-h-24 resize-none"
                      aria-invalid={fieldState.invalid}
                    />
                  </InputGroup>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <Controller
              name="terms"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field
                  data-invalid={fieldState.invalid}
                  orientation="horizontal"
                  className="col-span-1 md:col-span-2"
                >
                  <Checkbox
                    id="enquiry-form-terms"
                    name={field.name}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <FieldLabel htmlFor="enquiry-form-terms" className="font-normal">
                    <span>
                      I agree to Spoclearn{" "}
                      <Link href={"/terms-and-conditions"} className="text-primary font-semibold">
                        terms of use
                      </Link>{" "}
                      and privacy policy
                    </span>
                  </FieldLabel>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
          </FieldGroup>
        </form>
      </CardContent>
      <CardFooter className="bg-card border-0">
        <Field orientation="horizontal">
          <Button type="submit" onClick={form.handleSubmit(onSubmit)} disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Field>
      </CardFooter>
    </Card>
  );
}
